import { useEffect, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, AlertCircle } from 'lucide-react'
import { Drawer } from '../../components/ui/Drawer'
import { getContent, saveContent, type ContentEntity } from '../../lib/api/content'
import { cn } from '../../lib/cn'

type El = Record<string, unknown>
const isNum = (v: unknown) => v != null && v !== '' && Number.isFinite(Number(v))
const numStr = (v: unknown) => (isNum(v) ? String(Number(v)) : '')
const numVal = (s: string) => (Number.isFinite(Number(s)) ? Number(s) : 0)

const LANGS = [
  { code: 'it', label: 'Italiano' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
] as const
type Lang = (typeof LANGS)[number]['code']

type Trans = { label: string; description: string }
type Form = {
  name: string
  iconUri: string
  maxAvailability: string
  maxOnsitePaymentAvailability: string
  position: string
  tr: Record<Lang, Trans>
}
type Loaded = Record<Lang, ContentEntity>

const NEW = 'new'

function iconToStr(icon: unknown): string {
  return icon && typeof icon === 'object'
    ? String((icon as { url?: unknown; path?: unknown }).url ?? (icon as { path?: unknown }).path ?? '')
    : String(icon ?? '')
}
// "description" is an RTE/HTML field in the model (e.g. "<p>Altezza max 2,10 mt</p>"). Edit it
// as plain text (one line per paragraph) so admins never see tags; re-wrap to HTML on save.
function htmlToText(html: unknown): string {
  return String(html ?? '')
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}
function textToHtml(text: string): string {
  const t = text.replace(/\r\n/g, '\n').trim()
  if (!t) return ''
  return t
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => `<p>${l.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`)
    .join('')
}

function transOf(e: ContentEntity): Trans {
  const el = e.elements as El
  return { label: String(el.label ?? ''), description: htmlToText(el.description) }
}
function blankForm(): Form {
  return {
    name: '',
    iconUri: '',
    maxAvailability: '',
    maxOnsitePaymentAvailability: '',
    position: '0',
    tr: { it: { label: '', description: '' }, en: { label: '', description: '' }, de: { label: '', description: '' } },
  }
}
function seedForm(loaded: Loaded): Form {
  const el = loaded.it.elements as El
  return {
    name: String(el.name ?? ''),
    iconUri: iconToStr(el.iconUri),
    maxAvailability: numStr(el.maxAvailability),
    // the TRUE stored value — no masking, so saves are visible (9999 = illimitato)
    maxOnsitePaymentAvailability: numStr(el.maxOnsitePaymentAvailability),
    position: numStr(el.position),
    tr: { it: transOf(loaded.it), en: transOf(loaded.en), de: transOf(loaded.de) },
  }
}

/* ── field atoms (match the rates editor) ── */
function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-[1.2px] text-muted">{label}</span>
      {children}
      {hint && <span className="mt-0.5 block text-[11px] text-faint">{hint}</span>}
    </label>
  )
}
const inputCls =
  'h-9 w-full rounded-md border border-line bg-card px-3 text-[13px] text-coal outline-none focus:border-verdigris disabled:opacity-50'
const areaCls =
  'min-h-[80px] w-full rounded-md border border-line bg-card px-3 py-2 text-[13px] text-coal outline-none focus:border-verdigris disabled:opacity-50'

function fmtDate(v: unknown): string {
  if (v == null || v === '') return '—'
  const n = Number(v)
  const d = Number.isFinite(n) && n > 0 ? new Date(n < 1e12 ? n * 1000 : n) : new Date(String(v))
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('it-IT')
}

export function TransportEditDrawer({ transportId, onClose }: { transportId: string | null; onClose: () => void }) {
  const qc = useQueryClient()
  const isNew = transportId === NEW
  const [form, setForm] = useState<Form | null>(null)
  const [loaded, setLoaded] = useState<Loaded | null>(null)
  const [lang, setLang] = useState<Lang>('it')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const detail = useQuery({
    queryKey: ['transport', transportId, 'i18n'],
    queryFn: async (): Promise<Loaded> => {
      const [it, en, de] = await Promise.all([
        getContent('transport', transportId!, 'it'),
        getContent('transport', transportId!, 'en'),
        getContent('transport', transportId!, 'de'),
      ])
      return { it, en, de }
    },
    enabled: Boolean(transportId) && !isNew,
  })

  useEffect(() => {
    if (isNew) {
      setLoaded(null)
      setForm(blankForm())
      setLang('it')
      setError(null)
    } else if (detail.data) {
      setLoaded(detail.data)
      setForm(seedForm(detail.data))
      setLang('it')
      setError(null)
    }
  }, [isNew, detail.data])

  if (!transportId) return null
  const patch = (p: Partial<Form>) => setForm((f) => (f ? { ...f, ...p } : f))
  const patchTr = (l: Lang, p: Partial<Trans>) =>
    setForm((f) => (f ? { ...f, tr: { ...f.tr, [l]: { ...f.tr[l], ...p } } } : f))

  async function save() {
    if (!form) return
    if (!form.name.trim()) {
      setError('Il nome è obbligatorio.')
      return
    }
    if (form.maxAvailability === '' || Number(form.maxAvailability) < 0) {
      setError('Inserisci una disponibilità giornaliera valida.')
      return
    }
    setError(null)
    setSaving(true)
    try {
      if (isNew || !loaded) {
        // CREATE: save 'it' first with no id to create the entity, capture the new id,
        // then write the en/de translations onto it. Order matters (it creates; en/de update).
        let id = ''
        for (const { code } of LANGS) {
          const e: El = {
            name: form.name.trim(),
            iconUri: form.iconUri.trim(),
            maxAvailability: numVal(form.maxAvailability),
            maxOnsitePaymentAvailability: numVal(form.maxOnsitePaymentAvailability),
            position: numVal(form.position),
            label: form.tr[code].label.trim(),
            description: textToHtml(form.tr[code].description),
          }
          const saved = await saveContent('transport', { id, modelId: 'transport', elements: e }, code)
          if (!id) id = saved.id
        }
      } else {
        // EDIT: round-trip each language's loaded entity, overwrite shared + translated fields.
        const iconChanged = form.iconUri.trim() !== iconToStr((loaded.it.elements as El).iconUri)
        for (const { code } of LANGS) {
          const base = loaded[code]
          const next: ContentEntity = { ...base, elements: { ...(base.elements as El) } }
          const e = next.elements as El
          e.name = form.name.trim()
          e.label = form.tr[code].label.trim()
          e.description = textToHtml(form.tr[code].description)
          if (iconChanged) e.iconUri = form.iconUri.trim()
          e.maxAvailability = numVal(form.maxAvailability)
          e.maxOnsitePaymentAvailability = numVal(form.maxOnsitePaymentAvailability)
          e.position = numVal(form.position)
          await saveContent('transport', next, code)
        }
      }
      await qc.invalidateQueries({ queryKey: ['content', 'transport'] })
      await qc.invalidateQueries({ queryKey: ['transport', transportId] })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Salvataggio non riuscito.')
    } finally {
      setSaving(false)
    }
  }

  const meta = loaded?.it

  return (
    <Drawer
      open={Boolean(transportId)}
      onClose={onClose}
      tone="brand"
      title={<span className="text-[15px] font-bold text-white">{isNew ? 'Nuovo tipo di veicolo' : 'Modifica tipo di veicolo'}</span>}
      subtitle={isNew ? 'Crea una nuova categoria di veicolo' : form ? form.name || '—' : 'Caricamento…'}
      footer={
        <div className="flex items-center gap-3">
          {error ? (
            <span className="text-[12px] font-medium text-venetian">{error}</span>
          ) : (
            <span className="text-[11.5px] text-muted">Salva scrive sul gestionale live e sulla disponibilità di prenotazione.</span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={saving}
              className="rounded-lg border border-line bg-card px-4 py-2 text-[13px] font-semibold text-slate hover:border-faint disabled:opacity-50"
            >
              Annulla
            </button>
            <button
              onClick={save}
              disabled={saving || !form}
              className="inline-flex items-center gap-2 rounded-lg bg-verdigris px-4 py-2 text-[13px] font-semibold text-white hover:bg-verdigris-700 disabled:opacity-60"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {isNew ? 'Crea' : 'Salva'}
            </button>
          </div>
        </div>
      }
    >
      {(!isNew && detail.isLoading) || !form ? (
        <div className="flex items-center justify-center py-20 text-muted">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : !isNew && detail.isError ? (
        <div className="py-20 text-center text-sm text-muted">
          <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare il tipo di veicolo.
        </div>
      ) : (
        <div className="space-y-5">
          {/* shared (non-translated) fields */}
          <Field label="Nome" hint="Identificativo interno (non tradotto)">
            <input className={inputCls} value={form.name} disabled={saving} onChange={(e) => patch({ name: e.target.value })} />
          </Field>
          <Field label="Icona" hint="Percorso del file, es. booking-car.png">
            <input className={inputCls} value={form.iconUri} disabled={saving} onChange={(e) => patch({ iconUri: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Disponibilità giornaliera" hint="Posti prenotabili al giorno">
              <input type="number" min="0" className={inputCls} value={form.maxAvailability} disabled={saving} onChange={(e) => patch({ maxAvailability: e.target.value })} />
            </Field>
            <Field label="Pagabili in cassa" hint="9999 = illimitato">
              <input type="number" min="0" className={inputCls} value={form.maxOnsitePaymentAvailability} disabled={saving} onChange={(e) => patch({ maxOnsitePaymentAvailability: e.target.value })} />
            </Field>
          </div>
          <Field label="Posizione" hint="Ordine di visualizzazione">
            <input type="number" className={cn(inputCls, 'max-w-[140px]')} value={form.position} disabled={saving} onChange={(e) => patch({ position: e.target.value })} />
          </Field>

          {/* translated fields (label + description per language) */}
          <div className="rounded-lg border border-line-soft">
            <div className="flex items-center justify-between border-b border-line-soft px-3 pt-2">
              <span className="pb-2 text-[11px] font-bold uppercase tracking-[1.2px] text-muted">Traduzioni</span>
              <div className="flex gap-1">
                {LANGS.map(({ code, label }) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setLang(code)}
                    className={cn(
                      '-mb-px border-b-2 px-2.5 py-1.5 text-[12.5px] font-semibold transition-colors',
                      lang === code ? 'border-verdigris text-coal' : 'border-transparent text-muted hover:text-coal',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-3 p-3">
              <Field label="Label" hint="Etichetta mostrata sul sito di prenotazione">
                <input className={inputCls} value={form.tr[lang].label} disabled={saving} onChange={(e) => patchTr(lang, { label: e.target.value })} />
              </Field>
              <Field label="Descrizione" hint="Testo semplice — una riga per paragrafo">
                <textarea className={areaCls} value={form.tr[lang].description} disabled={saving} onChange={(e) => patchTr(lang, { description: e.target.value })} />
              </Field>
            </div>
          </div>

          {/* read-only metadata (parity with the legacy "Informazioni") */}
          {meta && (
            <div className="flex flex-wrap gap-x-6 gap-y-1 border-t border-line-soft pt-3 text-[12px] text-muted">
              <span>Creato: <span className="text-slate">{fmtDate(meta.creationDate)}</span></span>
              <span>Ultima modifica: <span className="text-slate">{fmtDate(meta.lastUpdate)}</span></span>
            </div>
          )}
        </div>
      )}
    </Drawer>
  )
}
